// PRE RMA — Registre de consultation (RCE) : choix du registre de travail et numéro suivant.
//
//   « RCE Unique »          : un seul registre pour tous les âges → une seule numérotation ;
//   « RCE < 5 ans »         : registre des enfants de moins de 5 ans → numérotation propre ;
//   « RCE 5 ans et plus »   : registre des 5 ans et plus → numérotation propre.
//
// Le registre n'est PAS enregistré avec le patient (aucun champ de base de données) : il se déduit de l'âge (5 ans = 1825 jours,
// seuil RULES.age.seuils.cinqAns) ; il sert à proposer le bon « prochain N° » et à éviter de saisir un patient dans le mauvais registre.
import { RULES } from './rules.config.ts';
import { parseAge } from './age.ts';

export type Registre = 'UNIQUE' | 'MOINS5' | 'PLUS5';
export const REGISTRES: ReadonlyArray<{ id: Registre; libelle: string; aide: string }> = [
  { id: 'UNIQUE', libelle: 'RCE Unique', aide: 'Un seul registre, tous âges' },
  { id: 'MOINS5', libelle: 'RCE < 5 ans', aide: 'Enfants de moins de 5 ans' },
  { id: 'PLUS5', libelle: 'RCE 5 ans et plus', aide: '5 ans et plus' },
];
export const REGISTRE_PAR_DEFAUT: Registre = 'UNIQUE';
export const estRegistre = (x: unknown): x is Registre => x === 'UNIQUE' || x === 'MOINS5' || x === 'PLUS5';
export const libelleRegistre = (r: Registre) => REGISTRES.find((x) => x.id === r)!.libelle;

const SEUIL = RULES.age.seuils.cinqAns;

/** Registre auquel appartient un âge (en jours) dans une organisation à deux registres. */
export const registreDeAge = (ageJours: number): Exclude<Registre, 'UNIQUE'> => (ageJours < SEUIL ? 'MOINS5' : 'PLUS5');

/** Numéros RCE saisis (« 12 », « 12, 13 »). « CRENAS » et le texte libre n'ont pas de numéro. */
export function numerosRce(numeroRceBrut: string | null | undefined): number[] {
  const v = (numeroRceBrut ?? '').trim();
  if (v === '' || v.toUpperCase() === 'CRENAS') return [];
  return v.split(',').map((s) => s.trim()).filter((s) => /^\d+$/.test(s)).map(Number);
}

export interface PatientNumerote { numeroRceBrut: string; ageBrut: string | null }

/** Plus grand numéro déjà utilisé dans le registre choisi (0 s'il est vide). */
export function dernierNumero(patients: PatientNumerote[], registre: Registre): number {
  let max = 0;
  for (const p of patients) {
    const nums = numerosRce(p.numeroRceBrut);
    if (!nums.length) continue;
    if (registre !== 'UNIQUE') {
      const age = parseAge(p.ageBrut);
      if (!age.ok || age.ageJours == null || registreDeAge(age.ageJours) !== registre) continue;
    }
    max = Math.max(max, ...nums);
  }
  return max;
}
export const prochainNumero = (patients: PatientNumerote[], registre: Registre) => dernierNumero(patients, registre) + 1;

/**
 * Contrôle « bon registre » : bloque la saisie d'un patient de 5 ans ou plus dans « RCE < 5 ans » (et inversement), car
 * cela fausserait la numérotation des deux registres. Aucun contrôle en « RCE Unique ». Âge invalide ou absent : null
 * (c'est le contrôle d'âge habituel qui s'en charge).
 */
export function controlerRegistre(registre: Registre, ageBrut: string | null | undefined): string | null {
  if (registre === 'UNIQUE') return null;
  const age = parseAge(ageBrut);
  if (!age.ok || age.ageJours == null) return null;
  const reel = registreDeAge(age.ageJours);
  if (reel === registre) return null;
  return registre === 'MOINS5'
    ? `L'âge saisi (${ageBrut?.trim()}) est de 5 ans ou plus : ce patient va dans le registre « ${libelleRegistre('PLUS5')} ». Changez de registre ou corrigez l'âge.`
    : `L'âge saisi (${ageBrut?.trim()}) est de moins de 5 ans : ce patient va dans le registre « ${libelleRegistre('MOINS5')} ». Changez de registre ou corrigez l'âge.`;
}
